import React, { useState, FormEvent, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Icons } from '../../components/common/Icons';
import { authApi } from '../../api/authApi';
import { isValidEmail } from '../../utils/helpers';
import { APP_NAME, APP_SUBTITLE } from '../../utils/constants';
import './ForgotPassword.css';
import omLogo from '../../images/images.jpg';

type Step = 'email' | 'otp' | 'password';

export function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      await authApi.forgotPassword(email.trim().toLowerCase());
      setStep('otp');
      setCountdown(60); // 60 second cooldown for resend
      setSuccess('If an account exists with this email, you will receive an OTP shortly.');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setOtp(pastedData.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleOtpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    try {
      setLoading(true);
      const response = await authApi.verifyOtp(email, otpString);
      setResetToken(response.reset_token);
      setStep('password');
      setSuccess('OTP verified! Please set your new password.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await authApi.resetPassword(resetToken, newPassword);
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;

    try {
      setLoading(true);
      setError(null);
      await authApi.forgotPassword(email);
      setOtp(['', '', '', '', '', '']);
      setCountdown(60);
      setSuccess('A new OTP has been sent to your email.');
      setTimeout(() => setSuccess(null), 5000);
      otpRefs.current[0]?.focus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => {
    const isEmailDone = step === 'otp' || step === 'password';
    const isOtpDone = step === 'password';
    
    return (
      <div className="step-indicator">
        <div className={`step ${step === 'email' ? 'active' : ''} ${isEmailDone ? 'completed' : ''}`}>
          <div className="step-number">{isEmailDone ? <Icons.Check /> : '1'}</div>
          <span>Email</span>
        </div>
        <div className="step-line" />
        <div className={`step ${step === 'otp' ? 'active' : ''} ${isOtpDone ? 'completed' : ''}`}>
          <div className="step-number">{isOtpDone ? <Icons.Check /> : '2'}</div>
          <span>Verify</span>
        </div>
        <div className="step-line" />
        <div className={`step ${step === 'password' ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <span>Reset</span>
        </div>
      </div>
    );
  };

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          <div className="forgot-password-header">
            <div className="forgot-password-logo">
              <img src={omLogo} alt="Logo" />
            </div>
            <div className="forgot-password-brand">
              <span className="brand-name">{APP_NAME}</span>
              <span className="brand-subtitle">{APP_SUBTITLE}</span>
            </div>
          </div>

          {renderStepIndicator()}

          <div className="forgot-password-form-section">
            {error && (
              <div className="forgot-password-error">
                <Icons.AlertCircle />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="forgot-password-success">
                <Icons.Check />
                <span>{success}</span>
              </div>
            )}

            {/* Step 1: Email */}
            {step === 'email' && (
              <>
                <div className="step-header">
                  <h1>Forgot Password?</h1>
                  <p>Enter your email address and we'll send you an OTP to reset your password.</p>
                </div>
                <form onSubmit={handleEmailSubmit} className="forgot-password-form">
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    leftIcon={<Icons.Email />}
                    autoComplete="email"
                    autoFocus
                  />
                  <Button type="submit" fullWidth size="lg" isLoading={loading}>
                    Send OTP
                  </Button>
                </form>
              </>
            )}

            {/* Step 2: OTP */}
            {step === 'otp' && (
              <>
                <div className="step-header">
                  <h1>Enter OTP</h1>
                  <p>We've sent a 6-digit code to <strong>{email}</strong></p>
                </div>
                <form onSubmit={handleOtpSubmit} className="forgot-password-form">
                  <div className="otp-input-group" onPaste={handleOtpPaste}>
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="otp-input"
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                  <Button type="submit" fullWidth size="lg" isLoading={loading} disabled={otp.join('').length !== 6}>
                    Verify OTP
                  </Button>
                  <div className="resend-section">
                    <span>Didn't receive the code? </span>
                    {countdown > 0 ? (
                      <span className="countdown">Resend in {countdown}s</span>
                    ) : (
                      <button type="button" className="resend-link" onClick={handleResendOtp} disabled={loading}>
                        Resend OTP
                      </button>
                    )}
                  </div>
                </form>
              </>
            )}

            {/* Step 3: New Password */}
            {step === 'password' && (
              <>
                <div className="step-header">
                  <h1>Set New Password</h1>
                  <p>Create a strong password to secure your account.</p>
                </div>
                <form onSubmit={handlePasswordSubmit} className="forgot-password-form">
                  <div className="password-field">
                    <Input
                      label="New Password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      leftIcon={<Icons.Lock />}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                    </button>
                  </div>

                  <div className="password-field">
                    <Input
                      label="Confirm Password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      leftIcon={<Icons.Lock />}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="password-requirements">
                    <p>Password must contain:</p>
                    <ul>
                      <li className={newPassword.length >= 8 ? 'valid' : ''}>
                        <Icons.Check /> At least 8 characters
                      </li>
                      <li className={/[A-Z]/.test(newPassword) ? 'valid' : ''}>
                        <Icons.Check /> One uppercase letter
                      </li>
                      <li className={/[a-z]/.test(newPassword) ? 'valid' : ''}>
                        <Icons.Check /> One lowercase letter
                      </li>
                      <li className={/[0-9]/.test(newPassword) ? 'valid' : ''}>
                        <Icons.Check /> One number
                      </li>
                      <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'valid' : ''}>
                        <Icons.Check /> One special character
                      </li>
                    </ul>
                  </div>

                  <Button
                    type="submit"
                    fullWidth
                    size="lg"
                    isLoading={loading}
                    disabled={!newPassword || !confirmPassword}
                  >
                    Reset Password
                  </Button>
                </form>
              </>
            )}

            <div className="back-to-login">
              <Link to="/login">
                <Icons.ChevronLeft />
                <span>Back to Sign In</span>
              </Link>
            </div>
          </div>
        </div>

        <p className="forgot-password-footer">
          &copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default ForgotPassword;
